<template>
  <div ref="rootContainer" :class="`pge-dropdown ${isActive ? 'active' : ''}`" @click="handleContainerClick">
    <div class="pge-dropdown__separated-label-text" v-if="labelType === 'separated'">
      {{ label }} <span v-if="required">*</span>
    </div>
    <div class="text-field">
      <div class="text-field">
        <div class="state-layer">
          <div class="content">
            <span v-if="selectedOption" class="content__icon">
              <icon-component :icon="selectedOption.icon" />
            </span>
            <div class="input-text">
              <input :id="inputId" class="input-text" type="text" :required="required" :placeholder="placeholder"
                @blur="handleBlurInput" @focus="handleFocusInput" @input="handleChangeInput"
                @keydown.enter="handleEnterKey" @keydown.down.prevent="moveDown" @keydown.up.prevent="moveUp"
                v-model="searchText" />
            </div>
            <div class="label-text" v-if="labelType === 'floated'">
              <label class="label-text" :for="inputId">{{ label }}</label>
            </div>
          </div>
          <div class="trailing-icon">
            <div class="pge-container">
              <div class="state-layer">
                <div class="icon"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="`pge-menu ${position === 'top' ? 'top' : ''}`" ref="menuContainer">
      <div class="text-scrollbar" ref="scrollbar">
        <div class="list-items">
          <div class="list-items-section" v-for="(group, groupName) in filteredOptions" :key="groupName">
            <div class="group-label">{{ groupName }}</div>
            <div v-for="(option, index) in group" :key="option.label"
              :class="{ 'dropdown-menu-item': true, 'pge-highlighted': index === selectedOptionIndex }"
              @mousedown="handleOnClickItem(option)">
              <span v-if="option.icon" class="dropdown-menu-item__icon">
                <icon-component :icon="option.icon" />
              </span>
              <span v-if="option.img" class="dropdown-menu-item__img">
                <icon-component v-if="option.img === 'Default'" icon="DefaultAvatar" />
                <img v-else width="24" height="24" :src="option.img" :alt="option.label" />
              </span>
              <span class="text-primary">{{ option.label }}</span>
              <span class="text-secondary" v-if="option.info">{{ option.info }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useId } from 'vue';
import { removeWhitespaces } from '../../helpers/helper';
import IconComponent from '../icons/index.vue';

export default {
  components: {
    IconComponent,
  },
  setup() {
    return { inputId: useId() };
  },
  props: {
    label: {
      type: String,
      default: 'Select'
    },
    labelType: {
      type: String,
      default: 'floated',
      validator: v => ['floated', 'separated'].includes(v)
    },
    placeholder: {
      type: String,
      default: 'Select'
    },
    options: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: String,
      default: ''
    },
    onChange: Function,
    position: {
      type: String,
      default: 'bottom'
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchText: this.findOptionByValue(this.modelValue)?.label || '',
      isActive: false,
      isTyping: false,
      selectedOption: this.findOptionByValue(this.modelValue) || null,
      selectedOptionIndex: -1,
    };
  },
  computed: {
    filteredOptions() {
      if (this.isActive && this.searchText) {
        const searchRegex = new RegExp(this.searchText.trim(), 'i');
        return Object.keys(this.options).reduce((acc, group) => {
          const filteredGroup = this.options[group].filter(option => searchRegex.test(option.label));
          if (filteredGroup.length) {
            acc[group] = filteredGroup;
          }
          return acc;
        }, {});
      } else {
        return this.options;
      }
    }
  },
  watch: {
    modelValue(value) {
      this.searchText = this.findOptionByValue(value)?.label || '';
      this.selectedOption = this.findOptionByValue(value);
    }
  },
  methods: {
    handleFocusInput() {
      if(this.position === 'bottom') {
        this.$refs.menuContainer.style.top = `${this.$refs.rootContainer.clientHeight}px`;
      }

      this.isActive = true;
      this.selectedOptionIndex = -1;
    },
    handleBlurInput(e) {
      setTimeout(() => {
        const option = this.findOptionByLabel(e.target.value);

        // Revert if not set
        if (!option) {
          const prevValue = this.findOptionByValue(this.modelValue);
          this.selectedOption = prevValue;
          this.$emit('update:modelValue', prevValue?.value);
          this.searchText = prevValue?.label;
        }

        this.isActive = false;
        this.isTyping = false;
        this.selectedOptionIndex = -1;

      }, 100);
    },
    handleChangeInput(e) {
      this.isTyping = true;
      this.searchText = e.target.value;
    },
    handleOnClickItem(option) {
      this.selectedOption = option;
      this.$emit('update:modelValue', option.value);
      this.$emit('change');
    },
    handleContainerClick(e) {
      const container = e.target.closest(".pge-dropdown");
      if (!container.classList.contains("active")) {
        container.querySelector("input.input-text").focus();
      }
    },
    handleKeyDown(e) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedOptionIndex = Math.max(this.selectedOptionIndex - 1, 0);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedOptionIndex = Math.min(this.selectedOptionIndex + 1, this.filteredOptions.length - 1);
      }
    },
    handleEnterKey(e) {
      if (this.selectedOptionIndex !== -1) {
        const option = this.filteredOptions[this.selectedOptionIndex];
        if (option) {
          this.searchText = option.label;
          e.target.closest(".pge-dropdown").querySelector("input.input-text").blur();
          this.$emit('update:modelValue', option.value);
        }
      }
    },
    moveDown() {
      const groupKeys = Object.keys(this.filteredOptions);

      if (this.selectedOptionIndex < this.filteredOptions[groupKeys[groupKeys.length - 1]].length - 1) {
        this.selectedOptionIndex++;
        this.scrollIntoView();
      }
    },
    moveUp() {
      if (this.selectedOptionIndex > 0) {
        this.selectedOptionIndex--;
        this.scrollIntoView();
      }
    },
    scrollIntoView() {
      const menuContainer = this.$refs.menuContainer;
      const scrollbar = this.$refs.scrollbar;
      const selectedItem = scrollbar.querySelectorAll('.dropdown-menu-item')[this.selectedOptionIndex];

      if (selectedItem) {
        const topOffset = selectedItem.offsetTop;
        const bottomOffset = topOffset + selectedItem.offsetHeight;

        if (bottomOffset > menuContainer.scrollTop + menuContainer.clientHeight) {
          menuContainer.scrollTop = bottomOffset - menuContainer.clientHeight;
        } else if (topOffset < menuContainer.scrollTop) {
          menuContainer.scrollTop = topOffset;
        }
      }
    },
    findOptionByLabel(label) {
      for (let group in this.options) {
        const option = this.options[group].find(option => removeWhitespaces(option.label) === removeWhitespaces(label));
        if (option) return option;
      }
      return null;
    },
    findOptionByValue(value) {
      for (let group in this.options) {
        const option = this.options[group].find(option => removeWhitespaces(option.value) === removeWhitespaces(value));
        if (option) return option;
      }
      return null;
    },
  }
};
</script>

<style>
@import url("./../styles/pge-dropdown/dropdown/index.scss");
@import url("./../styles/pge-dropdown/dropdown/active.scss");

.pge-dropdown .group-label {
  font-weight: bold;
  margin: 10px;
}
</style>
